import { Box, Flex, Progress, Text } from "@chakra-ui/react";
import React, { useEffect, useRef } from "react";

import {
  InviteUsersButton,
  LoadingIndicator,
  SearchInput,
  SettingsPageContainer,
  useToast,
} from "../../../components";
import type { MainInviteUserInput } from "../../../components/InviteUsersButton/types";
import useOffsetPagination from "../../../hooks/useOffsetPagination";
import { UserList } from "../../components";
import {
  PlanUserRolePermission,
  useInviteUsersMutation,
  useOrgUsersPaginatedLazyQuery,
  UserRoleName,
  UserRolePermission,
} from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import useCurrentUser from "../../hooks/useCurrentUser";

const UsersListPage: React.FC = () => {
  const toast = useToast();
  const currentUser = useCurrentUser();
  const screenSkuEnabled =
    useFeatureFlagForCurrentOrImpersonatedUser("ai_interviewer:v1");

  // Track setters in refs to avoid stale closure issues in useEffect
  const setNumberOfPagesRef = useRef<(n: number) => void>();
  const setListItemsRef = useRef<(items: any[]) => void>();

  const [getOrgUsers, { refetch, data, loading, error }] =
    useOrgUsersPaginatedLazyQuery({
      fetchPolicy: "network-only",
    });

  // Handle query completion via useEffect instead of deprecated onCompleted callback (Apollo 3.14+)
  useEffect(() => {
    if (data) {
      const userList = data?.currentUser?.organization?.usersPaginated;
      setNumberOfPagesRef.current?.(userList?.pageInfo?.totalPages || 1);
      setListItemsRef.current?.(userList?.results ?? []);
    }
  }, [data]);

  // Handle query errors via useEffect instead of deprecated onError callback (Apollo 3.14+)
  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: `Failed to load user data: ${error.message}`,
        status: "error",
      });
    }
  }, [error, toast]);
  const pageLimit = 100;
  const {
    listItems: users,
    setListItems,
    setNumberOfPages,
    pageOptions,
    setSearchTerm,
    searchTerm,
    didFetchComplete,
  } = useOffsetPagination<any>(
    (opts) =>
      opts?.variables?.pagination &&
      getOrgUsers({
        ...opts,
        variables: {
          deleted: false,
          signUpCompleted: false,
          pagination: opts?.variables.pagination,
        },
      }),
    {
      initialSortBy: { id: "createdAt", desc: true },
      pageLimit,
    }
  );

  // Wire up refs for use in useEffect callbacks
  setNumberOfPagesRef.current = setNumberOfPages;
  setListItemsRef.current = setListItems;
  const [inviteUsers] = useInviteUsersMutation({
    update(cache, { data: inviteUsersData }) {
      const newUsers = inviteUsersData?.inviteUsers?.users;
      if (newUsers) {
        cache.modify({
          id: cache.identify(currentUser.organization),
          fields: {
            users: (prevUsers) => {
              return [...prevUsers, ...newUsers];
            },
          },
        });
        refetch();
      }
    },
  });

  const usedFreemiumSeats = users.length;
  const totalFreemiumSeats =
    data?.currentUser?.organization?.freemiumSeatCount ?? 0;
  const availableFreemiumSeats = totalFreemiumSeats - usedFreemiumSeats;
  const isFreemium = !!data?.currentUser?.organization?.freemium;

  const canManageUserRoles = currentUser.userRole?.permissions?.includes(
    UserRolePermission.ManageUserRoles
  );
  const roles = canManageUserRoles
    ? currentUser.organization.userRoles
    : currentUser.organization.userRoles.filter(
        (r) => r.name === UserRoleName.Basic
      );
  // const canInviteUsers =
  //   (!isFreemium || usedFreemiumSeats < totalFreemiumSeats) &&
  //   (currentUser.userRole?.permissions?.includes(
  //     UserRolePermission.ManageHiringTeam
  //   ) ||
  //     currentUser.memberships.reduce((acc, membership) => {
  //       return (
  //         acc ||
  //         membership.role.permissions.includes(
  //           PositionRolePermission.ManageHiringTeam
  //         )
  //       );
  //     }, false));
  const canManageHiringTeam = currentUser.userRole?.canManageHiringTeam;
  const canManageAnyPositionHiringTeam = currentUser.memberships.some(
    (membership) => membership.role.canManageHiringTeam
  );
  let canInviteUsers = canManageHiringTeam || canManageAnyPositionHiringTeam;
  if (isFreemium && availableFreemiumSeats === 0) {
    canInviteUsers = false;
  }

  const planSkuEnabled = data?.currentUser?.organization?.planEnabled;
  const canManagePlanUserRoles =
    currentUser.planUserRole?.permissions?.includes(
      PlanUserRolePermission.ManageUserRoles
    );
  const planUserRoles = [
    {
      id: "",
      name: "NO_ACCESS",
      permissions: [],
      formattedPermissions: "No access to BrightHire Plan",
      formattedName: "No access",
    },
    ...(currentUser.organization.planUserRoles || []),
  ];
  const planRoles = canManagePlanUserRoles
    ? planUserRoles
    : planUserRoles.filter((r) => r.name === "NO_ACCESS");

  const canManageAiInterviewers =
    currentUser.userRole?.canManageAiInterviewers ?? false;
  const screenUserRoles = [
    {
      id: "",
      name: "NO_ACCESS",
      permissions: [],
      formattedPermissions: "No access to BrightHire Screen",
      formattedName: "No access",
    },
    ...(currentUser.organization.aiInterviewerUserRoles || []),
  ];
  const screenRoles = canManageAiInterviewers
    ? screenUserRoles
    : screenUserRoles.filter((r) => r.name === "NO_ACCESS");

  return (
    <SettingsPageContainer
      minW="max(900px, 100%)"
      heading={
        <Flex alignItems="start" justifyContent="space-between">
          <Text>Users list</Text>
          {isFreemium && (
            <Box>
              <Text fontSize="md" fontWeight="normal">
                <strong>{usedFreemiumSeats}</strong> of{" "}
                <strong>{totalFreemiumSeats}</strong> seats used
              </Text>
              <Progress
                value={usedFreemiumSeats}
                max={totalFreemiumSeats}
                size="sm"
                colorScheme={availableFreemiumSeats === 0 ? "red" : "blue"}
              />
            </Box>
          )}
        </Flex>
      }
      subHeading="View and manage all active and invited users."
    >
      <Flex alignItems="center" justifyContent="space-between" mb="4">
        <SearchInput
          onSearch={(searchString) => {
            setSearchTerm(searchString);
          }}
          defaultValue={searchTerm}
          placeholder="Search names, emails"
        />
        <InviteUsersButton<MainInviteUserInput>
          inviteUsers={(users) => {
            // TypeScript ensures users matches MainInviteUserInput
            // orgId is optional for main schema (inferred from current user)
            return inviteUsers({ variables: { users } }).then((res) => {
              if (res.errors) {
                throw res.errors[0].message;
              }
            });
          }}
          roles={roles}
          includePlanRoleField={planSkuEnabled}
          planRoles={planRoles}
          includeScreenRoleField={screenSkuEnabled}
          screenRoles={screenRoles}
          disabled={!canInviteUsers}
          maxInvites={
            isFreemium ? totalFreemiumSeats - usedFreemiumSeats : undefined
          }
        />
      </Flex>
      {loading ? (
        <LoadingIndicator />
      ) : didFetchComplete && users.length > 0 ? (
        <UserList
          users={users}
          pageOptions={pageOptions}
          sortBy={pageOptions.sortBy}
          planSkuEnabled={planSkuEnabled}
          canDisableUsers={canManageUserRoles}
        />
      ) : (
        <Box>
          <Text>
            No users found{searchTerm ? " matching your search." : "."}
          </Text>
        </Box>
      )}
    </SettingsPageContainer>
  );
};

export default UsersListPage;
